import { translate, translateRich, useLocale } from "../../localization";
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import AccountsAPI from "../../services/AccountsAPI";
import backgroundImage from "../../images/Sign-Up-Background.jpg";
import { AppButton, AppTextField } from "../../components/ui";
import AuthSplitPage from "./AuthSplitPage";
import TurnstileWidget from "../../components/security/TurnstileWidget";

const PASSWORD_RULES_MESSAGE =() => translate("Password must be at least 8 characters and include uppercase, lowercase, and a number.");
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY?.trim() || "";
const SIGN_UP_HONEYPOT_FIELD = "website";

const getApiErrorMessage = (err, statusMessages, fallbackMessage) => {
  const backendMessage = err?.response?.data?.message;
  const status = err?.response?.status;

  if (typeof backendMessage === "string" && backendMessage.trim()) {
    return backendMessage;
  }

  if (statusMessages?.[status]) {
    return statusMessages[status];
  }

  return fallbackMessage;
};

const isPasswordValid = (password) =>
  /[A-Z]/.test(password) &&
  /[a-z]/.test(password) &&
  /\d/.test(password) &&
  password.length >= 8;

const isEmailValid = (email) => /\S+@\S+\.\S+/.test(email);
const normalizeEmail = (value) => value.trim().toLowerCase();

const SignUp = () => {
  const locale = useLocale();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    organization: "",
    password: "",
    confirmPassword: "",
    [SIGN_UP_HONEYPOT_FIELD]: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmationCode, setConfirmationCode] = useState("");
  const [confirmationCodeError, setConfirmationCodeError] = useState("");
  const [step, setStep] = useState("details");
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [isSubmittingDetails, setIsSubmittingDetails] = useState(false);
  const [isConfirmingCode, setIsConfirmingCode] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileError, setTurnstileError] = useState("");
  const [turnstileResetTrigger, setTurnstileResetTrigger] = useState(0);

  const handleTurnstileTokenChange = useCallback((token) => {
    setTurnstileToken(token);
    if (token) {
      setTurnstileError("");
      setFieldErrors((prev) => ({
        ...prev,
        turnstile: "",
      }));
    }
  }, []);

  const handleTurnstileErrorChange = useCallback((message) => {
    setTurnstileError(message);
    setFieldErrors((prev) => ({
      ...prev,
      turnstile: message,
    }));
  }, []);

  useEffect(() => {
    if (step !== "success") {
      return undefined;
    }

    const timer = setTimeout(() => {
      navigate("/login", {
        replace: true,
        state: {
          accountMessage: translate("Account confirmed, please sign in."),
        },
      });
    }, 1500);

    return () => clearTimeout(timer);
  }, [navigate, step, locale]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (name === "password" && fieldErrors.confirmPassword) {
      setFieldErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }

    setFormError("");
  };

  const validateSignUpForm = () => {
    const errors = {};
    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    const email = normalizeEmail(formData.email);
    const organization = formData.organization.trim();

    if (!firstName) {
      errors.firstName = translate("First name is required.");
    }
    if (!lastName) {
      errors.lastName = translate("Last name is required.");
    }
    if (!email) {
      errors.email = translate("Email is required.");
    } else if (!isEmailValid(email)) {
      errors.email = translate("Enter a valid email address.");
    }
    if (!organization) {
      errors.organization = translate("Organization is required.");
    }
    if (!formData.password) {
      errors.password = translate("Password is required.");
    } else if (!isPasswordValid(formData.password)) {
      errors.password = PASSWORD_RULES_MESSAGE();
    }
    if (!formData.confirmPassword) {
      errors.confirmPassword = translate("Please confirm your password.");
    } else if (formData.confirmPassword !== formData.password) {
      errors.confirmPassword = translate("Passwords do not match.");
    }
    if (!TURNSTILE_SITE_KEY) {
      errors.turnstile =
        translate("Sign up is temporarily unavailable while the security check is being configured.");
    } else if (!turnstileToken) {
      errors.turnstile =
        turnstileError || translate("Please complete the security check.");
    }

    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!validateSignUpForm()) {
      return;
    }

    const payload = {
      firstName: formData.firstName.trim(),
      middleName: formData.middleName.trim(),
      lastName: formData.lastName.trim(),
      email: normalizeEmail(formData.email),
      organization: formData.organization.trim(),
      password: formData.password,
      turnstileToken,
      [SIGN_UP_HONEYPOT_FIELD]: formData[SIGN_UP_HONEYPOT_FIELD],
    };

    try {
      setFormError("");
      setFormSuccess("");
      setIsSubmittingDetails(true);

      await AccountsAPI.post("/sign-up", payload, { requiresAuth: false });

      setFormData((prev) => ({
        ...prev,
        firstName: payload.firstName,
        middleName: payload.middleName,
        lastName: payload.lastName,
        email: payload.email,
        organization: payload.organization,
      }));
      setStep("confirm");
      setConfirmationCode("");
      setConfirmationCodeError("");
      setFormSuccess(translate("We sent a confirmation code to {value0}.", {
  value0: payload.email
}));
    } catch (err) {
      setTurnstileToken("");
      setTurnstileResetTrigger((value) => value + 1);
      setFormError(
        getApiErrorMessage(
          err,
          {
            400: translate("Please review your details and password requirements, then try again."),
            409: translate("An account with this email already exists. Please sign in or reset your password."),
            429: translate("Too many signup attempts. Please wait a few minutes and try again."),
            503: translate("Sign up is temporarily unavailable right now. Please try again later."),
          },
          translate("Something went wrong while creating your account. Please try again.")
        )
      );
    } finally {
      setIsSubmittingDetails(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    const normalizedCode = confirmationCode.trim();

    if (!normalizedCode) {
      setConfirmationCodeError(translate("Confirmation code is required."));
      return;
    }

    try {
      setFormError("");
      setFormSuccess("");
      setIsConfirmingCode(true);

      await AccountsAPI.post(
        "/sign-up/confirm",
        {
          email: normalizeEmail(formData.email),
          confirmationCode: normalizedCode,
        },
        { requiresAuth: false }
      );

      setConfirmationCodeError("");
      setFormSuccess(translate("Account confirmed. Redirecting to sign in..."));
      setStep("success");
    } catch (err) {
      setFormError(
        getApiErrorMessage(
          err,
          {
            400: translate("That confirmation code is invalid or expired. Please try again or request a new code."),
            429: translate("Too many confirmation attempts. Please wait a few minutes and try again."),
          },
          translate("Unable to confirm your account right now. Please try again.")
        )
      );
    } finally {
      setIsConfirmingCode(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setFormError("");
      setFormSuccess("");
      setIsResendingCode(true);

      await AccountsAPI.post(
        "/sign-up/resend-code",
        {
          email: normalizeEmail(formData.email),
        },
        { requiresAuth: false }
      );

      setFormSuccess(translate("A new confirmation code was sent to {value0}.", {
  value0: formData.email
}));
    } catch (err) {
      setFormError(
        getApiErrorMessage(
          err,
          {
            400: translate("We couldn't resend a code for this account. Please restart signup if needed."),
            429: translate("Too many resend requests. Please wait a few minutes before trying again."),
          },
          translate("Unable to resend confirmation code right now. Please try again.")
        )
      );
    } finally {
      setIsResendingCode(false);
    }
  };

  const features = [
    translate("Full access to 10k Science Viewer with NGSS-aligned experiences on laptops and desktops"),
    translate("Insights Dashboard with AI-assisted student analytics"),
    translate("Join Codes for your students"),
    translate("AI audio guides for students"),
  ];

  return (
    <AuthSplitPage
      heroBackgroundImage={backgroundImage}
      heroCardMaxWidth={800}
      formFirstOnMobile={false}
      heroPanelSx={{
        p: { xs: 2, sm: 3, md: 4, lg: 10, xl: 20 },
        "&::before": {
          backgroundColor: "rgba(15, 23, 42, 0.12)",
        },
      }}
      hero={
        <Box>
          <Typography variant="h1" component="h1" gutterBottom>{translate("See how 10k Science can be used in your classroom")}</Typography>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            <strong>{translate("Your trial includes:")}</strong>
          </Typography>
          <List sx={{ p: 0, mt: { xs: 2, sm: 3 } }}>
            {features.map((feature, index) => (
              <ListItem
                key={index}
                disableGutters
                sx={{
                  alignItems: "flex-start",
                  py: { xs: 0.5, sm: 1 },
                  gap: 1.5,
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mt: "2px", color: "success.main" }}>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={feature}
                  primaryTypographyProps={{
                    align: "left",
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      }
      formContentMaxWidth={400}
      form={
        <Box
          width="100%"
          maxWidth={400}
          p={{ xs: 3, sm: 4 }}
          bgcolor="var(--color-bg-card)"
          boxSizing="border-box"
          sx={{
            borderRadius: "16px",
            boxShadow: "var(--shadow-card-hover, 0 12px 36px rgba(0, 0, 0, 0.18))",
          }}
        >
          {step === "details" ? (
            <form onSubmit={handleSignUp} noValidate>
              <Box marginBottom={2}>
                <Typography
                  variant="h2"
                  component="h1"
                  align="center"
                  gutterBottom
                >{translate("Start your journey with a 7-day free trial.")}</Typography>
                <Typography variant="body2" align="center">{translate("No credit card required.")}</Typography>
              </Box>

              {formError && (
                <Box marginBottom={2}>
                  <Alert severity="error">{formError}</Alert>
                </Box>
              )}
              {formSuccess && (
                <Box marginBottom={2}>
                  <Alert severity="success">{formSuccess}</Alert>
                </Box>
              )}

              {!TURNSTILE_SITE_KEY && (
                <Box marginBottom={2}>
                  <Alert severity="warning">{translate("Sign up is temporarily unavailable while the security check is being configured.")}</Alert>
                </Box>
              )}

              <AppTextField
                label={translate("First Name")}
                fullWidth
                margin="normal"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={Boolean(fieldErrors.firstName)}
                helperText={fieldErrors.firstName}
                required
              />
              <AppTextField
                label={translate("Middle Name")}
                fullWidth
                margin="normal"
                name="middleName"
                value={formData.middleName}
                onChange={handleChange}
              />
              <AppTextField
                label={translate("Last Name")}
                fullWidth
                margin="normal"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={Boolean(fieldErrors.lastName)}
                helperText={fieldErrors.lastName}
                required
              />
              <AppTextField
                label={translate("Email")}
                type="email"
                fullWidth
                margin="normal"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
                required
              />
              <AppTextField
                label={translate("Organization")}
                fullWidth
                margin="normal"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                error={Boolean(fieldErrors.organization)}
                helperText={fieldErrors.organization}
                required
              />
              <AppTextField
                label={translate("Password")}
                type="password"
                fullWidth
                margin="normal"
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={Boolean(fieldErrors.password)}
                helperText={fieldErrors.password}
                required
              />
              <AppTextField
                label={translate("Confirm Password")}
                type="password"
                fullWidth
                margin="normal"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={Boolean(fieldErrors.confirmPassword)}
                helperText={fieldErrors.confirmPassword}
                required
              />

              <Box
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  left: "-10000px",
                  top: "auto",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                }}
              >
                <AppTextField
                  label={translate("Website")}
                  fullWidth
                  margin="normal"
                  name={SIGN_UP_HONEYPOT_FIELD}
                  value={formData[SIGN_UP_HONEYPOT_FIELD]}
                  onChange={handleChange}
                  autoComplete="off"
                  tabIndex={-1}
                  inputProps={{
                    tabIndex: -1,
                  }}
                />
              </Box>

              <TurnstileWidget
                siteKey={TURNSTILE_SITE_KEY}
                resetTrigger={turnstileResetTrigger}
                onTokenChange={handleTurnstileTokenChange}
                onErrorChange={handleTurnstileErrorChange}
              />
              {fieldErrors.turnstile && (
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 1, color: "error.main" }}
                >
                  {fieldErrors.turnstile}
                </Typography>
              )}

              <Box display="flex" justifyContent="center" marginTop={2}>
                <AppButton
                  type="submit"
                  kind="primary"
                  fullWidth
                  loading={isSubmittingDetails}
                  loadingLabel={translate("Creating account...")}
                  disabled={isSubmittingDetails || !TURNSTILE_SITE_KEY}
                >{translate("Start Free Trial")}</AppButton>
              </Box>
            </form>
          ) : (
            <form onSubmit={handleConfirmSignUp} noValidate>
              <Box marginBottom={2}>
                <Typography
                  variant="h2"
                  component="h1"
                  align="center"
                  gutterBottom
                >{translate("Confirm your account")}</Typography>
                <Typography variant="body2" align="center">{translateRich("Enter the confirmation code sent to {value0}.", {
  value0: formData.email
})}</Typography>
              </Box>

              {formError && (
                <Box marginBottom={2}>
                  <Alert severity="error">{formError}</Alert>
                </Box>
              )}
              {formSuccess && (
                <Box marginBottom={2}>
                  <Alert severity="success">{formSuccess}</Alert>
                </Box>
              )}

              <AppTextField
                label={translate("Confirmation Code")}
                fullWidth
                margin="normal"
                name="confirmationCode"
                value={confirmationCode}
                onChange={(e) => {
                  setConfirmationCode(e.target.value);
                  if (confirmationCodeError) {
                    setConfirmationCodeError("");
                  }
                  setFormError("");
                }}
                error={Boolean(confirmationCodeError)}
                helperText={confirmationCodeError}
                required
                disabled={step === "success"}
              />

              <Box display="flex" flexDirection="column" gap={1.5} marginTop={2}>
                <AppButton
                  type="submit"
                  kind="primary"
                  fullWidth
                  loading={isConfirmingCode}
                  loadingLabel={translate("Confirming...")}
                  disabled={isConfirmingCode || isResendingCode || step === "success"}
                >{translate("Confirm Account")}</AppButton>
                <AppButton
                  type="button"
                  kind="ghost"
                  fullWidth
                  loading={isResendingCode}
                  loadingLabel={translate("Resending...")}
                  onClick={handleResendCode}
                  disabled={isConfirmingCode || isResendingCode || step === "success"}
                >{translate("Resend code")}</AppButton>
              </Box>
            </form>
          )}
        </Box>
      }
    />
  );
};

export default SignUp;
